import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'wouter';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

type BrandLink = { id: number; brandId: number; brandName: string; role: 'represented' | 'involved'; confirmedAt: string };
type BrandOption = { id: number; name: string };

export function DealBrandsPanel({ dealId }: { dealId: number }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<'represented' | 'involved'>('represented');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const queryKey = [`/api/deals/${dealId}/brands`];
  const { data: links = [], isLoading, isError, refetch } = useQuery<BrandLink[]>({
    queryKey,
    queryFn: () => apiRequest(`/api/deals/${dealId}/brands`),
    enabled: Number.isSafeInteger(dealId) && dealId > 0,
  });
  const term = search.trim();
  const { data: options = [], isFetching } = useQuery<BrandOption[]>({
    queryKey: ['/api/brands', { q: term, purpose: 'deal-link' }],
    queryFn: () => apiRequest(`/api/brands?q=${encodeURIComponent(term)}`),
    enabled: term.length >= 2,
  });

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey }),
      queryClient.invalidateQueries({ predicate: (query) => typeof query.queryKey[0] === 'string' && /^\/api\/brands\/\d+\/deals$/.test(query.queryKey[0]) }),
    ]);
  };
  const add = async (brandId: number) => {
    setBusy(true); setError(null); setMessage(null);
    try {
      await apiRequest({ url: `/api/deals/${dealId}/brands`, method: 'POST', data: { brandId, role }, notifySuccess: false, notifyError: false });
      setSearch('');
      await refresh();
      setMessage('Brand collegato al Deal.');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Collegamento non riuscito');
    } finally { setBusy(false); }
  };
  const remove = async (link: BrandLink) => {
    if (!window.confirm(`Scollegare ${link.brandName} da questo Deal? Il collegamento resta nello storico.`)) return;
    setBusy(true); setError(null); setMessage(null);
    try {
      await apiRequest({ url: `/api/deals/${dealId}/brands/${link.id}`, method: 'DELETE', notifySuccess: false, notifyError: false });
      await refresh();
      setMessage('Brand scollegato. Il collegamento resta nello storico.');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Scollegamento non riuscito');
    } finally { setBusy(false); }
  };

  return <section aria-label="Brand del Deal" className="space-y-3">
    <div>
      <h3 className="text-sm font-semibold">Brand del Deal</h3>
      <p className="mt-1 text-xs leading-5 text-muted-foreground">Collega solo Brand confermati per questa trattativa. L'Azienda del Deal non crea collegamenti automatici.</p>
    </div>
    {isLoading && <p className="text-sm text-muted-foreground">Caricamento Brand...</p>}
    {isError && <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>Riprova caricamento Brand</Button>}
    {!isLoading && !isError && (links.length ? <ul className="space-y-2">
      {links.map((link) => <li key={link.id} className="flex min-w-0 items-center gap-2 border border-border bg-background px-3 py-2 text-sm">
        <Link href={`/brands/${link.brandId}`} className="min-w-0 flex-1 truncate text-primary hover:underline">{link.brandName}</Link>
        <span className="shrink-0 text-xs text-muted-foreground">{link.role === 'represented' ? 'Rappresentato' : 'Coinvolto'}</span>
        <Button type="button" variant="ghost" size="sm" className="shrink-0" disabled={busy} onClick={() => void remove(link)} aria-label={`Scollega ${link.brandName}`}>Scollega</Button>
      </li>)}
    </ul> : <p className="text-sm text-muted-foreground">Nessun Brand collegato.</p>)}
    <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
      <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Cerca un Brand per nome" aria-label="Cerca Brand da collegare" />
      <select value={role} onChange={(event) => setRole(event.target.value as 'represented' | 'involved')} aria-label="Ruolo del Brand nel Deal" className="h-10 rounded-md border border-input bg-background px-3 text-sm">
        <option value="represented">Brand rappresentato</option>
        <option value="involved">Brand coinvolto</option>
      </select>
    </div>
    {term.length >= 2 && <div className="max-h-44 overflow-y-auto border border-border" aria-label="Risultati Brand">
      {isFetching && <p className="px-3 py-2 text-xs text-muted-foreground">Ricerca...</p>}
      {!isFetching && options.filter((brand) => !links.some((link) => link.brandId === brand.id)).map((brand) =>
        <button type="button" key={brand.id} disabled={busy} onClick={() => void add(brand.id)} className="block w-full border-b border-border px-3 py-2 text-left text-sm hover:bg-muted disabled:opacity-50">{brand.name} <span className="text-xs text-muted-foreground">· Collega</span></button>)}
      {!isFetching && options.length === 0 && <p className="px-3 py-2 text-xs text-muted-foreground">Nessun Brand trovato.</p>}
    </div>}
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {message && <p role="status" className="text-sm text-muted-foreground">{message}</p>}
  </section>;
}
